import React, { useCallback, useMemo, useState } from 'react';
import {
  ProfileHeader,
  InfoCard,
  NotificationPreferences,
  SecuritySettings,
  SettingsSection,
  LogoutButton,
} from './components';
import {
  WarehouseProfile as WarehouseProfileType,
  NotificationPreference,
  defaultNotificationPreferences,
  defaultSecurityOptions,
  defaultSettingsItems,
} from './profileTypes';
import { useAuth, isUserActive } from '../../../contexts/AuthContext';
import { ChangePasswordModal } from '../../../components/ChangePasswordModal';
import styles from './WarehouseProfile.module.css';

const WarehouseProfile: React.FC = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<NotificationPreference[]>(defaultNotificationPreferences);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const handleTogglePreference = useCallback((id: string) => {
    setPreferences((prev) => prev.map((p) => (p.id === id ? { ...p, enabled: !p.enabled } : p)));
  }, []);

  const handleSecurityClick = useCallback((id: string) => {
    if (id === 'change-password') setShowPasswordModal(true);
  }, []);

  const profile: WarehouseProfileType = useMemo(() => ({
    id: user?.id ?? 'N/A',
    staffName: user?.full_name ?? 'N/A',
    role: user?.role ?? 'Warehouse Manager',
    status: isUserActive(user) ? 'Active' : 'Inactive',
    assignedWarehouse: user?.warehouse_code || 'N/A',
  }), [user]);

  // All fields show the admin-configured value from /users/me (migration 027);
  // any column that's still NULL renders 'N/A' so the UI never lies about
  // having data that isn't actually persisted.
  const personalFields = [
    { label: 'Staff Name', value: profile.staffName },
    { label: 'Employee ID', value: user?.employee_id || 'N/A' },
    { label: 'Role', value: profile.role },
    { label: 'Assigned Warehouse', value: user?.warehouse_code || 'N/A' },
  ];

  const contactFields = [
    { label: 'Email', value: user?.email ?? 'N/A' },
    { label: 'Phone', value: user?.phone || 'N/A' },
    { label: 'Department', value: user?.department || 'N/A' },
  ];

  return (
    <div className={styles.container}>
      <ProfileHeader profile={profile} />

      <div className={styles.infoGrid}>
        <InfoCard title="Personal Information" icon="personal" fields={personalFields} />
        <InfoCard title="Contact Information" icon="contact" fields={contactFields} />
      </div>

      <NotificationPreferences preferences={preferences} onToggle={handleTogglePreference} />
      <SecuritySettings options={defaultSecurityOptions} onOptionClick={handleSecurityClick} />
      <SettingsSection items={defaultSettingsItems} />
      <LogoutButton />

      <p className={styles.version}>VIN pulse OS v2.1.0</p>

      <ChangePasswordModal
        open={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />
    </div>
  );
};

export const WarehouseProfilePage: React.FC = () => {
  return <WarehouseProfile />;
};

export { WarehouseProfile };
export default WarehouseProfile;
